/**
 * HTML to Markdown conversion utility.
 * Used for markdown preview and export in the Wiki editor.
 *
 * L5 — Codex retest 2026-05-08 surfaced two related bugs:
 *
 *   1. Underline was emitted as `__text__`, which is markdown's *bold*
 *      delimiter, not underline. Round-tripping through any markdown
 *      reader (including the chat MarkdownRenderer used by L1) rendered
 *      the underlined run as bold — silently corrupting authored
 *      content.
 *   2. Adjacent inline-format runs combined into ambiguous markdown
 *      sequences (e.g. `**A**` immediately followed by `*B*` rendered
 *      as `**A***B*`). CommonMark technically resolves this, but some
 *      parsers and our reader stripped the wrong runs, leaving "messy
 *      `**`/`*`" output.
 *
 * The fix: emit underline as `<u>...</u>` (MarkdownRenderer keeps
 * sanitized HTML inline, so this round-trips visually). Also tolerate
 * Tiptap's optional tag attributes, handle code blocks first so we
 * don't transform inline code.
 *
 * #199 — round 3 retest found two regressions in the round-1 patch:
 *
 *   a) The `<u>...</u>` HTML emitted in step (5) was being silently
 *      stripped by the catch-all "remaining tags" pattern in step (7)
 *      (`/<[^>]*>/g`). That regex matches `<u>` and `</u>` because
 *      `u` and `/u` are valid `[^>]*` content. End result: every
 *      authored underline disappeared on Preview MD and on Export.
 *      Fix: lift `<u>...</u>` into a placeholder before the strip
 *      and restore them afterwards, mirroring the codeBlocks
 *      pattern.
 *
 *   b) The zero-width-space boundary marker between adjacent inline
 *      runs is correct for markdown parsers but invisible in the
 *      Preview MD source pane, so users saw `**boldrun***italicrun*`
 *      and read it as broken markdown ("smashed adjacent runs").
 *      Fix: emit a regular space instead of ZWSP when one inline
 *      run closes and another opens with no whitespace between
 *      them. CommonMark handles `**A** *B*` as bold-A, space,
 *      italic-B — same rendered semantics, but the source is
 *      visibly clean.
 */

// Placeholder sentinel — a single Start-of-Heading control character
// (U+0001). Cannot appear in HTML / markdown source authored via the
// editor, never matches `[^>]*` inside a tag, never lands in final
// output. Used to lift code blocks and underline tags out of the
// catch-all regex sweeps and back in unchanged.
const SENT = String.fromCharCode(1);

export interface HtmlToMarkdownOptions {
  /**
   * Move every heading down this many levels (h1 → h2 at 1), capped at h6.
   * For output that puts its own `# ` title above the body.
   */
  headingOffset?: number;
}

export function htmlToMarkdown(html: string | undefined | null, opts: HtmlToMarkdownOptions = {}): string {
  if (!html) return '';

  try {
    let md = html;

    // 1) Code blocks first so we don't accidentally transform their
    //    contents. Use a placeholder + restore-at-end pattern.
    const codeBlocks: string[] = [];
    md = md.replace(/<pre[^>]*>\s*<code[^>]*>([\s\S]*?)<\/code>\s*<\/pre>/gi, (_m, body) => {
      codeBlocks.push(`\n\n\`\`\`\n${body}\n\`\`\`\n\n`);
      return `${SENT}CODE${codeBlocks.length - 1}${SENT}`;
    });

    // 1b) Heading shift (code blocks are already lifted, so their text is
    //     untouched). One pass rewrites each tag once — no cascade.
    const offset = Math.max(0, Math.trunc(opts.headingOffset ?? 0));
    if (offset > 0) {
      md = md.replace(/<(\/?)h([1-6])(?=[\s>])/gi, (_m, slash: string, level: string) =>
        `<${slash}h${Math.min(6, Number(level) + offset)}`,
      );
    }

    // 2) Block-level: headings, blockquotes, lists, paragraphs.
    md = md.replace(/<h1[^>]*>([\s\S]*?)<\/h1>/gi, '# $1\n\n');
    md = md.replace(/<h2[^>]*>([\s\S]*?)<\/h2>/gi, '## $1\n\n');
    md = md.replace(/<h3[^>]*>([\s\S]*?)<\/h3>/gi, '### $1\n\n');
    md = md.replace(/<h4[^>]*>([\s\S]*?)<\/h4>/gi, '#### $1\n\n');
    md = md.replace(/<h5[^>]*>([\s\S]*?)<\/h5>/gi, '##### $1\n\n');
    md = md.replace(/<h6[^>]*>([\s\S]*?)<\/h6>/gi, '###### $1\n\n');

    // Lists. Strip the surrounding ul/ol; treat each li as a hyphen
    // bullet. Tiptap doesn't deeply nest lists in editor output very
    // often; this keeps top-level bullets correct.
    md = md.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, '$1\n');
    md = md.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, '$1\n');
    md = md.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, '- $1\n');

    md = md.replace(/<blockquote[^>]*>([\s\S]*?)<\/blockquote>/gi, '> $1\n\n');

    md = md.replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, '$1\n\n');

    // 3) Inline code (after pre/code is already extracted).
    md = md.replace(/<code[^>]*>([\s\S]*?)<\/code>/gi, '`$1`');

    // 4) Inline formatting. #199 — emit a SENT-bracketed boundary
    //    sentinel at the closing edge so step (9) can resolve it
    //    contextually:
    //      * if the next char is another inline marker (`*` `_`
    //        `` ` `` `<`), insert a single visible space —
    //        CommonMark sees two distinct runs (`**A** *B*`) and
    //        the source view is clean
    //      * otherwise drop the sentinel (the original boundary
    //        already had natural whitespace from the surrounding
    //        text, or was end-of-line)
    const BOUND = `${SENT}B${SENT}`;
    md = md.replace(/<strong[^>]*>([\s\S]*?)<\/strong>/gi, `**$1**${BOUND}`);
    md = md.replace(/<b[^>]*>([\s\S]*?)<\/b>/gi, `**$1**${BOUND}`);
    md = md.replace(/<em[^>]*>([\s\S]*?)<\/em>/gi, `*$1*${BOUND}`);
    md = md.replace(/<i[^>]*>([\s\S]*?)<\/i>/gi, `*$1*${BOUND}`);

    // 5) Underline → HTML inline. MarkdownRenderer sanitises HTML
    //    and preserves `<u>`. We protect the tags from the catch-all
    //    "strip remaining tags" regex below by lifting them into
    //    placeholders, mirroring the codeBlocks pattern. (#199 fix)
    const underlineTags: string[] = [];
    md = md.replace(/<u[^>]*>([\s\S]*?)<\/u>/gi, (_m, body) => {
      underlineTags.push(`<u>${body}</u>`);
      return `${SENT}U${underlineTags.length - 1}${SENT}`;
    });

    // 6) Links + images.
    md = md.replace(/<a [^>]*href="(.*?)"[^>]*>([\s\S]*?)<\/a>/gi, '[$2]($1)');
    md = md.replace(/<img[^>]*src="(.*?)"[^>]*alt="(.*?)"[^>]*\/?>/gi, '![$2]($1)');
    md = md.replace(/<img[^>]*src="(.*?)"[^>]*\/?>/gi, '![]($1)');

    // 7) <br> and any remaining tags. The strip is intentionally
    //    aggressive — sanitised content like `<span>`, `<div>`,
    //    `<hr>` should not leak into markdown output. Underline
    //    tags are already lifted into placeholders above (#199);
    //    code blocks are already lifted at step (1).
    md = md.replace(/<br\s*\/?>/gi, '  \n');
    md = md.replace(/<[^>]*>/g, '');

    // 8) HTML entity decoding for the common ones.
    md = md
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    // 9) Resolve the inline-run boundary sentinels. The next char
    //    decides whether we need a visible separator or can drop
    //    the boundary entirely.
    const boundEsc = `${SENT}B${SENT}`.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    md = md.replace(new RegExp(`${boundEsc}(?=[*_\`<])`, 'g'), ' ');
    md = md.replace(new RegExp(boundEsc, 'g'), '');

    // 10) Restore underline placeholders (after the catch-all strip
    //     has run, so they survive into the final output).
    md = md.replace(new RegExp(`${SENT}U(\\d+)${SENT}`, 'g'), (_m, idx: string) => underlineTags[Number(idx)] ?? '');

    // 11) Restore code blocks.
    md = md.replace(new RegExp(`${SENT}CODE(\\d+)${SENT}`, 'g'), (_m, idx: string) => codeBlocks[Number(idx)] ?? '');

    // 12) Collapse extra blank lines + trim trailing whitespace per
    //     line so a stray space from step (9) doesn't trail forever.
    md = md.replace(/\n{3,}/g, '\n\n');
    md = md.replace(/[ \t]+$/gm, '');
    md = md.trim();

    return md;
  } catch (err) {
    // Last-resort handler: surface the original HTML so the user can
    // still copy authored content even if the converter chokes.
    // eslint-disable-next-line no-console
    console.error('Markdown conversion failed', err);
    return html || '';
  }
}

/**
 * The article reader's Download (#689): the title is the file's one `# `
 * heading and the body's headings sit one level under it (h1 → `## `).
 * The Wiki editor's export prepends no title, so it calls htmlToMarkdown
 * with the levels as written.
 */
export function articleMarkdownFile(title: string, html: string | undefined | null): string {
  return `# ${title}\n\n${htmlToMarkdown(html, { headingOffset: 1 })}\n`;
}
